/* eslint-disable no-empty-function */
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAlertUpdater } from '../../context/AlertContext';
import {
	useCustomerId,
	useIntervieweeId,
	useJobId,
	useRequesterId,
	useShowAddRequest,
	useSpaData,
	useSubmittalId,
} from '../../hooks';
import {
	useAssignFeedbackRequestMutation,
	useGetFeedbacksFormsQuery,
	useSearchPeopleMutation,
} from '../../services/feedbackQueries';
import { cleanFeedbackRequests } from '../FeedbackDashboard/FeedbackTab/Feedbacks/feedbacksUtils';
import MultipleInterviewersSelector from './MultipleInterviewersSelector';

const classes = {
	gridItemContent: {
		width: '100%',
	},
	buttonWrapper: {
		margin: (theme) => theme.spacing(1),
		position: 'relative',
	},
	circularProgress: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		marginTop: -12,
		marginLeft: -12,
	},
};

const isEmpty = (obj) =>
	obj === null || (Object.keys(obj).length === 0 && obj.constructor === Object);
const isEmptyVal = (val) => val === null || val.length === 0;

const FEEDBACK_REQUEST_CREATED_TOAST = 'toasts.feedback_request_created';
const FEEDBACK_REQUEST_CREATED_ERROR_TOAST = 'toasts.error';

const debounceWaitMillis = 1000;
const AddRequestDialog = ({ onSuccess = () => {}, onClose = () => {} }) => {
	const { t } = useTranslation(['candidate-feedback', 'common']);
	const { hideAddFeedbackRequestButton } = useSpaData();
	const { alert } = useAlertUpdater();

	const dialogRef = useRef(null);
	const [display, setDisplay] = useShowAddRequest();
	const customerId = useCustomerId();
	const submittalId = useSubmittalId();
	const jobId = useJobId();
	const requesterId = useRequesterId();
	const intervieweeId = useIntervieweeId();

	const [assignee, setAssignee] = useState([]);
	const [selectedForm, setSelectedForm] = useState(null);

	const [disableConfirm, setDisableConfirm] = useState(true);
	const [people, setPeople] = useState([]);
	const [numOfOptions, setNumOfOptions] = useState(0);
	const [numOfForms, setNumOfForms] = useState(0);
	const { data: forms = [] } = useGetFeedbacksFormsQuery(display);
	const { isPending: loadingPeople, mutate: queryPeople } = useSearchPeopleMutation();
	const { mutate: assignFeedbackForm, isPending: creatingFeedbackRequest } =
		useAssignFeedbackRequestMutation(submittalId);
	const {
		cancelLabel,
		addFeedbackRequestLabel,
		assignFeedbackLabel,
		assignInterviewerLabel,
		selectFormLabel,
		noOptionsText,
		loadingText,
		typeToSearchTextForMultiple,
		searchingPeople,
		optionsText,
		clearSelectionLabel,
		autocompleteArrowNavigation,
		formSelectionNavigation,
		formsOptionsText,
		noPermissionToAddFeedbackRequestText,
	} = useMemo(
		() => ({
			cancelLabel: t('common:cancel'),
			addFeedbackRequestLabel: t('add_feedback_request'),
			assignFeedbackLabel: t('assign_feedback'),
			assignInterviewerLabel: t('assign_interviewer'),
			selectFormLabel: t('select_form'),
			noOptionsText: t('no_options'),
			loadingText: t('searching_people'),
			typeToSearchTextForMultiple: t('type_to_search_multiple_options_allowed', { max: 5 }),
			searchingPeople: t('searching_people'),
			optionsText: t('number_of_options_available', { count: numOfOptions }),
			clearSelectionLabel: t('clear_selection'),
			autocompleteArrowNavigation: numOfOptions > 0 ? t('use_arrows_to_navigate') : '',
			formSelectionNavigation: numOfForms > 0 ? t('use_arrows_to_navigate') : '',
			formsOptionsText: t('number_of_options_available', { count: numOfForms }),
			noPermissionToAddFeedbackRequestText: t('no_permission_to_add_feedback_request'),
		}),
		[numOfForms, numOfOptions, t],
	);

	useEffect(() => {
		if (display) {
			setTimeout(() => {
				const focusable = dialogRef?.current?.querySelector(
					"button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
				);
				focusable?.focus();
				// eslint-disable-next-line no-magic-numbers
			}, 100);
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
	}, [display]);

	useEffect(() => {
		setDisableConfirm(isEmptyVal(assignee) || isEmpty(selectedForm) || creatingFeedbackRequest);
	}, [assignee, selectedForm, creatingFeedbackRequest]);

	useEffect(() => {
		const numberOfOptions = people.filter(
			(person) => person.name.giveName || person.name.surname,
		).length;
		setNumOfOptions(numberOfOptions);
	}, [people]);

	useEffect(() => {
		setNumOfForms(forms.length);
	}, [forms]);

	const debouncedQueryPeople = useMemo(
		() =>
			debounce((term) => {
				queryPeople(
					{ term },
					{
						onSuccess: ({ people: peopleOptions }) => setPeople(peopleOptions),
						onError: () => setPeople([]),
					},
				);
			}, debounceWaitMillis),
		[queryPeople],
	);
	const handleSearchChange = useCallback(
		(event) => {
			const searchTerm = event.target.value;
			debouncedQueryPeople(searchTerm);
		},
		[debouncedQueryPeople],
	);

	const getOptionLabelFromOption = ({ name, email }) => {
		const { givenName = '', surname = '' } = name;
		const workEmail = { ...email.find(({ isPrimary }) => isPrimary === true) }.address;
		if (workEmail) {
			return `${givenName} ${surname}, ${workEmail}`;
		}
		return `${givenName} ${surname}`;
	};

	const resetState = useCallback(() => {
		setDisplay(false);
		setAssignee([]);
		setSelectedForm(null);
		setPeople([]);
	}, [setDisplay, setAssignee, setSelectedForm, setPeople]);

	const handleClose = useCallback(() => {
		onClose();

		resetState();
	}, [onClose, resetState]);

	const handleCreateFeedbackRequest = async () => {
		const assigneeId = assignee.map(({ id }) => id);
		const cleanFeedbackRequestData = cleanFeedbackRequests({
			assigneeId,
			formId: selectedForm.formId,
			submittalId,
			customerId,
			jobId,
			requesterId,
			intervieweeId,
		});
		await assignFeedbackForm(cleanFeedbackRequestData, {
			onSuccess: () => {
				onSuccess();
				resetState();
				alert({ message: FEEDBACK_REQUEST_CREATED_TOAST });
			},
			onError: () => {
				alert({
					message: FEEDBACK_REQUEST_CREATED_ERROR_TOAST,
				});
			},
		});
	};

	return (
		<Dialog
			open={display}
			onClose={handleClose}
			aria-labelledby="form-dialog-title"
			fullWidth
			tabIndex={-1}
			ref={dialogRef}
			maxWidth="md"
			sx={{
				'& .MuiBackdrop-root': {
					zIndex: '0',
				},
			}}
			disableEnforceFocus
			disableAutoFocus
			disableScrollLock
		>
			<DialogTitle>
				<Typography
					variant="h3"
					component="span"
					id="form-dialog-title"
					data-testid="form-dialog-title"
				>
					{addFeedbackRequestLabel}
				</Typography>
			</DialogTitle>
			<DialogContent>
				{(() => {
					if (hideAddFeedbackRequestButton) {
						return (
							<Alert severity="error" role="alert" aria-live="assertive">
								{noPermissionToAddFeedbackRequestText}
							</Alert>
						);
					}
					return (
						<MultipleInterviewersSelector
							labels={{
								loadingPeople,
								loadingText,
								assignInterviewerLabel,
								typeToSearchTextForMultiple,
								noOptionsText,
								searchingPeople,
								optionsText,
								clearSelectionLabel,
								autocompleteArrowNavigation,
								formSelectionNavigation,
								selectFormLabel,
								formsOptionsText,
							}}
							classes={classes}
							people={people}
							getOptionLabelFromOption={getOptionLabelFromOption}
							setAssignee={setAssignee}
							assignee={assignee}
							handleSearchChange={handleSearchChange}
							selectedForm={selectedForm}
							setSelectedForm={setSelectedForm}
							forms={forms}
							setNumOfForms={setNumOfForms}
						/>
					);
				})()}
			</DialogContent>
			<DialogActions>
				<Box sx={classes.buttonWrapper}>
					<Button data-testid="close-button-testid" onClick={handleClose} color="primary">
						{cancelLabel}
					</Button>
				</Box>
				<Box sx={classes.buttonWrapper}>
					<Button
						onClick={handleCreateFeedbackRequest}
						data-testid="assign-feedback-button"
						color="primary"
						disabled={disableConfirm}
					>
						{assignFeedbackLabel}
					</Button>
					{creatingFeedbackRequest ? (
						<CircularProgress sx={classes.circularProgress} size={24} />
					) : (
						<></>
					)}
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default AddRequestDialog;
